import { useEffect } from "react";
import { createPortal } from "react-dom";
import { SignInButton, SignUpButton } from "@clerk/react";
import { X, Sparkles, LogIn, Star, Zap, CheckCircle2, Lock } from "lucide-react";

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  /** When set, shows the user's first frame (blurred) as a locked teaser. */
  previewImageUrl?: string | null;
}

const PERKS = [
  "42 free stars on signup",
  "Create images & videos with AI",
  "No credit card required",
];

export function AuthModal({ isOpen, onClose, title, previewImageUrl }: AuthModalProps) {
  const redirectUrl =
    typeof window !== "undefined"
      ? window.location.pathname + window.location.search
      : "/";

  useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Gated-generation scenario: keep the focus on the user's own (blurred)
  // generation with a single Sign Up CTA. No perks list, no big secondary
  // button — just the teaser and the one action that unlocks it.
  if (previewImageUrl) {
    return createPortal(
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-md"
          onClick={onClose}
        />
        <div
          className="relative w-full max-w-[400px] rounded-3xl overflow-hidden shadow-[0_32px_100px_rgba(0,0,0,0.45)] animate-modal-in"
          style={{ zIndex: 1 }}
        >
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-20 p-1.5 rounded-xl bg-black/25 backdrop-blur-sm text-white/70 hover:text-white hover:bg-black/45 transition-all duration-200 border border-white/10"
          >
            <X className="size-4" />
          </button>

          <div className="relative h-[min(560px,82vh)] w-full">
            <img
              src={previewImageUrl}
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
              style={{ filter: "blur(15px)", transform: "scale(1.25)" }}
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/35 to-black/90" />

            <div className="relative h-full flex flex-col items-center justify-end text-center px-7 pb-8">
              <div className="size-14 rounded-full bg-white/15 backdrop-blur-md border border-white/25 flex items-center justify-center mb-4">
                <Lock className="size-6 text-white" />
              </div>
              <h2 className="text-[24px] font-extrabold text-white tracking-tight drop-shadow-lg leading-tight">
                {title || "Sign Up to View Generation"}
              </h2>
              <p className="text-[13px] text-white/80 mt-2 mb-6 drop-shadow max-w-[280px]">
                Sign up to view &amp; download your generation in full quality.
              </p>

              <SignUpButton
                mode="modal"
                forceRedirectUrl={redirectUrl}
                signInForceRedirectUrl={redirectUrl}
              >
                <button
                  onClick={onClose}
                  className="relative w-full py-3.5 rounded-xl bg-gradient-to-r from-[#f0923a] to-[#e8822e] text-white text-[15px] font-bold flex items-center justify-center gap-2 shadow-lg shadow-orange-500/30 hover:shadow-xl hover:shadow-orange-500/40 hover:-translate-y-px active:translate-y-0 transition-all duration-200 overflow-hidden group"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/25 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                  <Star className="size-4 fill-white/30" />
                  Sign Up Free
                  <Sparkles className="size-3.5" />
                </button>
              </SignUpButton>

              <SignInButton
                mode="modal"
                forceRedirectUrl={redirectUrl}
                signUpForceRedirectUrl={redirectUrl}
              >
                <button
                  onClick={onClose}
                  className="mt-3.5 text-[12.5px] text-white/65 hover:text-white font-medium transition-colors duration-200"
                >
                  Already have an account?{" "}
                  <span className="underline underline-offset-2">Log in</span>
                </button>
              </SignInButton>
            </div>
          </div>
        </div>
      </div>,
      document.body,
    );
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[9999]"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-md"
        onClick={onClose}
        style={{ zIndex: 0 }}
      />

      <div
        className="relative w-full max-w-[400px] rounded-3xl overflow-hidden shadow-[0_32px_100px_rgba(0,0,0,0.3)] animate-modal-in"
        style={{ zIndex: 1, margin: "0 16px" }}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-20 p-1.5 rounded-xl bg-black/20 backdrop-blur-sm text-white/60 hover:text-white hover:bg-black/40 transition-all duration-200 border border-white/10"
        >
          <X className="size-4" />
        </button>

        {/* Hero banner with gradient background */}
        <div className="relative px-7 pt-8 pb-6 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-[#e8822e] via-[#f0923a] to-[#d4712a]" />
          <div className="absolute inset-0 bg-[url('/couch-mascot.png?v=5')] bg-no-repeat bg-[length:120px] bg-[position:right_-10px_bottom_-15px] opacity-20" />
          <div className="absolute top-0 right-0 w-40 h-40 rounded-full bg-white/10 blur-2xl -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-32 h-32 rounded-full bg-black/10 blur-2xl translate-y-1/2 -translate-x-1/2" />

          <div className="relative">
            <div className="flex items-center gap-2 mb-3">
              <img src="/couch-mascot.png?v=5" alt="CouchStack" className="size-10 drop-shadow-lg" />
              <div className="flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-white/20 backdrop-blur-sm border border-white/20 text-[10px] font-bold text-white/90">
                <Zap className="size-2.5" fill="currentColor" />
                AI-Powered
              </div>
            </div>
            <h2 className="text-[22px] font-extrabold text-white tracking-tight leading-tight">
              {title || "Start Creating\nAmazing Art"}
            </h2>
            <p className="text-[13px] text-white/70 mt-1.5">
              Join thousands making AI art & videos
            </p>
          </div>
        </div>

        {/* Content area */}
        <div className="bg-white px-7 pt-5 pb-6">
          {/* Perks */}
          <div className="flex flex-col gap-2 mb-5">
            {PERKS.map((perk) => (
              <div key={perk} className="flex items-center gap-2.5">
                <CheckCircle2 className="size-4 text-emerald-500 shrink-0" />
                <span className="text-[13px] text-[#5a5545] font-medium">{perk}</span>
              </div>
            ))}
          </div>

          {/* Buttons */}
          <div className="space-y-2.5">
            <SignUpButton
              mode="modal"
              forceRedirectUrl={redirectUrl}
              signInForceRedirectUrl={redirectUrl}
            >
              <button
                onClick={onClose}
                className="relative w-full py-3 rounded-xl bg-gradient-to-r from-[#f0923a] to-[#e8822e] text-white text-[14px] font-bold flex items-center justify-center gap-2 shadow-lg shadow-orange-500/25 hover:shadow-xl hover:shadow-orange-500/30 hover:-translate-y-px active:translate-y-0 transition-all duration-200 overflow-hidden group"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                <Star className="size-4 fill-white/30" />
                Sign Up Free
                <Sparkles className="size-3.5" />
              </button>
            </SignUpButton>

            <SignInButton
              mode="modal"
              forceRedirectUrl={redirectUrl}
              signUpForceRedirectUrl={redirectUrl}
            >
              <button
                onClick={onClose}
                className="w-full py-2.5 rounded-xl bg-[#faf6ed] border border-[rgba(45,49,39,0.08)] text-[14px] font-semibold flex items-center justify-center gap-2 text-[#5a5545] hover:bg-[#f0ebe0] hover:border-[rgba(45,49,39,0.12)] active:scale-[0.99] transition-all duration-200"
              >
                <LogIn className="size-4" />
                Already have an account? Log In
              </button>
            </SignInButton>
          </div>

          <p className="text-[10px] text-[#b5b0a0] text-center mt-4">
            No credit card required · Cancel anytime
          </p>
        </div>
      </div>
    </div>,
    document.body,
  );
}
